import { useState, useMemo } from 'react';
import AdminLayout from '../components/AdminLayout';
import { fetchAdminActorsForExport } from '../../../api/adminApi';

const CURRENT_YEAR = new Date().getFullYear();

const GENDERS = [
  { key: 'ALL', label: '전체' },
  { key: 'MALE', label: '남성' },
  { key: 'FEMALE', label: '여성' },
];

// 계정 이메일(email)은 항상 존재하고, 연락처 이메일(contactEmail)은 배우가 입력했을 때만 있다
const EMAIL_SOURCES = [
  { key: 'account', label: '계정 이메일' },
  { key: 'contact', label: '연락처 이메일 우선' },
];

const pickEmail = (actor, source) =>
  source === 'contact'
    ? (actor.contactEmail || actor.email || '')
    : (actor.email || '');

/** 출생연도 → 만 나이(근사) */
const toAge = (birthYear) => {
  const y = Number(birthYear);
  return Number.isFinite(y) ? CURRENT_YEAR - y : null;
};

function downloadCsv(rows, filename) {
  // BOM을 붙여야 엑셀에서 한글이 깨지지 않는다
  const header = ['이름', '성별', '출생연도', '이메일', '마케팅 수신 동의'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map(r => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminTalentEmailExportPage() {
  const [gender, setGender] = useState('ALL');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [emailSource, setEmailSource] = useState('account');
  // 광고성 정보 발송에는 수신 동의가 필요하므로 기본값을 켜둔다
  const [onlyConsented, setOnlyConsented] = useState(true);

  const [actors, setActors] = useState(null); // null = 아직 조회 안 함
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [diag, setDiag] = useState(null); // { source, rawCount }
  const [copied, setCopied] = useState(false);

  const rangeInvalid =
    yearFrom !== '' && yearTo !== '' && Number(yearFrom) > Number(yearTo);

  // 수신 동의 필터는 조회 후 화면에서 적용 — 체크만 바꿔도 재조회가 필요 없다
  const filteredActors = useMemo(() => {
    if (!actors) return null;
    return onlyConsented ? actors.filter(a => a.marketingAgreed) : actors;
  }, [actors, onlyConsented]);

  const excludedByConsent = actors && filteredActors ? actors.length - filteredActors.length : 0;

  // 이메일이 있는 배우만 추출 대상 — 중복 주소는 한 번만
  const emails = useMemo(() => {
    if (!filteredActors) return [];
    const seen = new Set();
    const out = [];
    for (const a of filteredActors) {
      const e = pickEmail(a, emailSource).trim();
      if (!e || seen.has(e.toLowerCase())) continue;
      seen.add(e.toLowerCase());
      out.push(e);
    }
    return out;
  }, [filteredActors, emailSource]);

  const missingEmailCount = filteredActors
    ? filteredActors.filter(a => !pickEmail(a, emailSource).trim()).length
    : 0;

  const handleSearch = async () => {
    if (rangeInvalid || loading) return;
    setLoading(true);
    setError(null);
    setCopied(false);
    setProgress(null);
    try {
      const { actors: list, source, rawCount } = await fetchAdminActorsForExport({
        gender,
        birthYearFrom: yearFrom,
        birthYearTo: yearTo,
        onProgress: (loaded, total) => setProgress({ loaded, total }),
      });
      setActors(list);
      setDiag({ source, rawCount });
    } catch (err) {
      const status = err.response?.status;
      setError(
        status
          ? `배우 목록을 불러오지 못했습니다. (HTTP ${status})`
          : (err.message || '배우 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.')
      );
      setActors(null);
      setDiag(null);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleReset = () => {
    setGender('ALL');
    setYearFrom('');
    setYearTo('');
    setEmailSource('account');
    setOnlyConsented(true);
    setActors(null);
    setDiag(null);
    setError(null);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (emails.length === 0) return;
    const text = emails.join(', ');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // 클립보드 권한이 없는 환경 폴백
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCsv = () => {
    if (!filteredActors || filteredActors.length === 0) return;
    const rows = filteredActors.map(a => [
      a.name ?? '',
      String(a.gender ?? '').toUpperCase() === 'MALE' ? '남' : '여',
      a.birthYear ?? '',
      pickEmail(a, emailSource),
      a.marketingAgreed ? '동의' : '미동의',
    ]);
    const parts = [
      gender === 'ALL' ? '전체' : gender === 'MALE' ? '남성' : '여성',
      yearFrom || '시작',
      yearTo || '끝',
    ];
    downloadCsv(rows, `배우이메일_${parts.join('_')}.csv`);
  };

  return (
    <AdminLayout pageId="actor-emails">
      <div className="admin-topbar">
        <h1>배우 이메일 추출</h1>
        <div className="admin-topbar__right">
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      {/* 필터 */}
      <div className="ag-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
              성별
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              {GENDERS.map(g => (
                <button
                  key={g.key}
                  className={`ag-filter-chip${gender === g.key ? ' active' : ''}`}
                  onClick={() => setGender(g.key)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
              출생연도
            </label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                className="ag-field__input"
                type="number"
                inputMode="numeric"
                placeholder="예: 1990"
                value={yearFrom}
                onChange={e => setYearFrom(e.target.value)}
                style={{ width: 110 }}
              />
              <span style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>~</span>
              <input
                className="ag-field__input"
                type="number"
                inputMode="numeric"
                placeholder={`예: ${CURRENT_YEAR}`}
                value={yearTo}
                onChange={e => setYearTo(e.target.value)}
                style={{ width: 110 }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="ag-btn ag-btn--primary"
              onClick={handleSearch}
              disabled={loading || rangeInvalid}
            >
              <i className="ti ti-search" /> {loading ? '조회 중...' : '조회'}
            </button>
            <button className="ag-btn" onClick={handleReset} disabled={loading}>
              초기화
            </button>
          </div>
        </div>

        {/* 추출 옵션 — 조회 없이 즉시 반영된다 */}
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
              추출할 이메일
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              {EMAIL_SOURCES.map(s => (
                <button
                  key={s.key}
                  className={`ag-filter-chip${emailSource === s.key ? ' active' : ''}`}
                  onClick={() => setEmailSource(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', paddingBottom: 6 }}>
            <input
              type="checkbox"
              checked={onlyConsented}
              onChange={e => setOnlyConsented(e.target.checked)}
            />
            마케팅 수신 동의자만
          </label>
        </div>

        <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 10 }}>
          연도를 비워두면 해당 방향 제한 없이 조회합니다. (예: 시작만 1990 → 1990년생 이후 전부)
          <br />
          계정 이메일은 항상 존재하고, 연락처 이메일은 배우가 입력한 경우에만 있습니다 (없으면 계정 이메일로 대체).
          테스트 계정은 자동 제외됩니다.
        </p>
        {!onlyConsented && (
          <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 6 }}>
            <i className="ti ti-alert-triangle" /> 수신 미동의자가 포함됩니다. 광고성 정보 발송에는 사용하지 마세요.
          </p>
        )}
        {rangeInvalid && (
          <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 6 }}>
            <i className="ti ti-alert-triangle" /> 시작 연도가 끝 연도보다 큽니다.
          </p>
        )}
      </div>

      {loading && progress && (
        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
          불러오는 중... {progress.loaded.toLocaleString()} / {progress.total.toLocaleString()}
        </div>
      )}

      {error && (
        <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      {actors && (
        <>
          {/* 결과 요약 + 추출 버튼 */}
          <div
            className="ag-card"
            style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
          >
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                대상 {filteredActors.length.toLocaleString()}명 · 이메일 {emails.length.toLocaleString()}건
              </div>
              {excludedByConsent > 0 && (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                  수신 미동의 {excludedByConsent.toLocaleString()}명 제외 (조회 {actors.length.toLocaleString()}명 중)
                </div>
              )}
              {missingEmailCount > 0 && (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                  이메일이 없는 배우 {missingEmailCount.toLocaleString()}명은 추출에서 제외됩니다.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="ag-btn ag-btn--primary" onClick={handleCopy} disabled={emails.length === 0}>
                <i className="ti ti-copy" /> {copied ? '복사됨!' : '이메일 복사'}
              </button>
              <button className="ag-btn" onClick={handleCsv} disabled={filteredActors.length === 0}>
                <i className="ti ti-download" /> CSV 다운로드
              </button>
            </div>
          </div>

          {/* 이메일 텍스트 — 그대로 복사해 붙여넣을 수 있도록 */}
          {emails.length > 0 && (
            <div className="ag-card" style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
                이메일 목록 (쉼표 구분)
              </label>
              <textarea
                readOnly
                value={emails.join(', ')}
                onFocus={e => e.target.select()}
                style={{
                  width: '100%', minHeight: 90, resize: 'vertical',
                  fontSize: 12, lineHeight: 1.6, padding: 10,
                  border: '1px solid var(--color-border)', borderRadius: 8,
                  fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          {/* 배우 목록 */}
          <div className="ag-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="ag-table">
                <thead>
                  <tr>
                    {['이름', '성별', '출생연도', '나이', '이메일', '수신 동의'].map(h => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filteredActors.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="ag-table__empty">
                        <i className="ti ti-search" style={{ fontSize: 20, display: 'block', marginBottom: 8 }} />
                        조건에 맞는 배우가 없습니다
                      </td>
                    </tr>
                  ) : filteredActors.map(a => {
                    const mail = pickEmail(a, emailSource);
                    return (
                      <tr key={a.id}>
                        <td style={{ fontSize: 13, fontWeight: 500 }}>{a.name}</td>
                        <td style={{ color: 'var(--color-text-secondary)' }}>
                          {String(a.gender ?? '').toUpperCase() === 'MALE' ? '남' : '여'}
                        </td>
                        <td style={{ color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                          {a.birthYear ?? '-'}
                        </td>
                        <td style={{ color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                          {toAge(a.birthYear) ?? '-'}
                        </td>
                        <td style={{ color: 'var(--color-text-secondary)' }}>
                          {mail || <span style={{ color: 'var(--color-danger)' }}>없음</span>}
                        </td>
                        <td>
                          <span className={`ag-badge ${a.marketingAgreed ? 'ag-badge--success' : 'ag-badge--gray'}`}>
                            {a.marketingAgreed ? '동의' : '미동의'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {diag && (
            <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 10 }}>
              <i className="ti ti-info-circle" />{' '}
              {diag.source === 'export'
                ? `전용 추출 API 응답 ${diag.rawCount.toLocaleString()}건`
                : `전용 추출 API가 없어 전체 목록 ${diag.rawCount.toLocaleString()}명을 불러와 화면에서 걸렀습니다`}
              {diag.rawCount === 0 && ' — 서버가 0건을 반환했습니다. 백엔드 배포 여부를 확인해주세요.'}
            </p>
          )}
        </>
      )}
    </AdminLayout>
  );
}
